/**
 * Calculate the Euclidean distance between two points represented as objects {x, y}.
 * @param {Object} point1 - The first point, with properties x and y.
 * @param {Object} point2 - The second point, with properties x and y.
 * @returns {number} - The distance between the two points.
 */
export function calculateDistance(point1, point2) {
    const dx = point2.x - point1.x;
    const dy = point2.y - point1.y;
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Finds the closest point to a reference point from an array of points.
 * 
 * @param {Array<{x: number, y: number}>} points - Array of points, each represented as an object with coordinates {x, y}.
 * @param {{x: number, y: number}} referencePoint - The reference point with coordinates {x, y}.
 * @returns {{x: number, y: number} | null} - The closest point to the reference point, or null if the array is empty.
 */
export function findClosestPoint(points, referencePoint) {
    if (points.length === 0) return null; // If the array is empty, return null

    let closestPoint = {...points[0], index:0};
    let minDistance = calculateDistance(closestPoint, referencePoint);

    for (let i = 1; i < points.length; i++) {
        const currentPoint = points[i];
        const distance = calculateDistance(currentPoint, referencePoint);

        if (distance < minDistance) {
            closestPoint = {...currentPoint[0], index:i};
            minDistance = distance;
        }
    }

    return closestPoint;
}

export function calculateAngleABC(A, B, C) {
    // Calcul des vecteurs BA et BC
    const BAx = A.x - B.x;
    const BAy = A.y - B.y;
    const BCx = C.x - B.x;
    const BCy = C.y - B.y;
    
    // Calcul du produit scalaire des vecteurs BA et BC
    const dotProduct = BAx * BCx + BAy * BCy;
    
    // Calcul des normes (longueurs) des vecteurs BA et BC
    const magnitudeBA = Math.sqrt(BAx * BAx + BAy * BAy);
    const magnitudeBC = Math.sqrt(BCx * BCx + BCy * BCy);
    
    // Calcul de l'angle en radians entre les vecteurs BA et BC
    let angleRad = Math.acos(dotProduct / (magnitudeBA * magnitudeBC));
    
    // Convertir l'angle en degrés
    let angleDeg = angleRad * (180 / Math.PI);

    // Pour déterminer l'orientation (sens horaire ou anti-horaire)
    const crossProduct = BAx * BCy - BAy * BCx;

    // Si le produit vectoriel est négatif, l'angle est dans le sens horaire (négatif)
    if (crossProduct < 0) {
        angleDeg = -angleDeg;
    }

    return angleDeg;
}

export function normalizeAngle(angle) {
    // Ramener l'angle entre -180 et 180
    while (angle > 180) angle -= 360;
    while (angle < -180) angle += 360;
    return angle;
}